/**
 * The control panel's view of edge-api.
 *
 * FOUR RULES, and the first three are borrowed unchanged from the contract
 * register because they were right there and are right here:
 *
 *   1. EVERY CALL TAKES AN AbortSignal. React 19 in StrictMode double-invokes
 *      effects in development, and a screen the reader navigated away from must
 *      not land its response on top of the screen they navigated to. There is no
 *      convenience overload without a signal, deliberately — an optional
 *      parameter is an optional parameter and it gets omitted.
 *
 *   2. NO RETRIES, ANYWHERE. A GET that failed shows an error; the reader
 *      presses reload. A silent retry turns one slow request into three and
 *      turns a 500 into a 15-second spinner, and on the PATCH path a retry is
 *      an outright bug: `agents.update` is not idempotent from the reader's
 *      point of view, because a second write can clobber a concurrent edit.
 *
 *   3. MONEY IS A STRING AND STAYS ONE. `costUsd` is `numeric(12,6)` in
 *      Postgres and arrives as a string precisely so it survives JSON without
 *      a float rounding it. Nothing here parses it; see `fmtCost` in
 *      state-pill.tsx, which formats it with string operations only.
 *
 *   4. THE SHARED TYPES COME FROM '@voiceai/wire' AND ARE NOT RESTATED. The
 *      wire package is the contract between the browser bundle and the Durable
 *      Object, and a second copy of `SessionState` in this file would be a
 *      second copy that drifts. What IS declared here is the shape of the
 *      READ-ONLY diagnostic payloads (`SessionDetail` and friends), which the
 *      wire contract does not cover: they are projections of the Neon tables in
 *      @voiceai/db, so each interface below names the table it mirrors and the
 *      column names are the drizzle field names verbatim. If the two ever
 *      disagree, @voiceai/db wins and this file is wrong.
 *
 * `req` AND `ApiError` ARE SHARED WITH lib/auth.ts AND NOTHING ELSE. The panel
 * worker answers the human-auth routes itself and proxies everything under /api
 * to edge-api, so they are two servers — but they send the same error envelope,
 * and one parser for it is the whole reason the helper is exported rather than
 * copied. Everything ABOVE the helper stays edge-api's; users and sessions of
 * the human kind live in lib/auth.ts.
 */
import type {
  AgentConfig,
  GreetingAudioInfo,
  CreateSessionRequest,
  CreateSessionResponse,
  EvidenceState,
  RecordingState,
  SessionState,
  SessionSummary,
  LiveConversation,
} from '@voiceai/wire'

export type { AgentConfig, EvidenceState, GreetingAudioInfo, RecordingState, SessionState, SessionSummary }

/**
 * Same-origin, always. vite.config.ts proxies /api to `wrangler dev` on 8787
 * rather than pointing the browser at a second origin, so the production CORS
 * posture — deny by default, no allowed origins at all — is what development
 * exercises too. An absolute base URL here would quietly opt out of that.
 */
const BASE = '/api'

/**
 * A failed request, with the status attached.
 *
 * The status matters to callers in exactly one place and it is worth the class:
 * a 404 on a session detail page is "no such session", which is a legitimate
 * screen, while a 500 is "the register is broken", which is a red box. Those
 * two must not render the same way.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Machine-readable code from the server body, when it sent one. */
    readonly code?: string,
    /**
     * Seconds from `Retry-After`, when the server sent one. Only a 429 does.
     *
     * It is on the error rather than left in the response because the only
     * screen that needs it — the login form, telling somebody how long they are
     * locked out for — is several layers above the fetch, and "too many
     * attempts" with no number is the kind of message people retry instantly.
     */
    readonly retryAfterSeconds?: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  get notFound(): boolean {
    return this.status === 404
  }
}

interface ReqInit {
  signal: AbortSignal
  method?: 'GET' | 'PATCH' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  /**
   * What the path is relative to. Defaults to `/api`, which is every route in
   * this file.
   *
   * `lib/auth.ts` passes `''` for the panel worker's own `/auth/*` routes. The
   * prefix says WHO ANSWERS: `/api/*` is proxied to edge-api with a machine key
   * attached server-side, while `/auth/*` is answered by the panel itself and
   * never forwarded. Putting a login under `/api` would be a lie about which
   * server holds the cookie.
   */
  base?: string
}

/**
 * What to do when a request discovers the session is gone.
 *
 * WHY IT IS A HOOK IN THE TRANSPORT AND NOT A CHECK IN EVERY SCREEN. A session
 * expires on its own schedule — twelve hours idle, fourteen days absolute, or the
 * moment an admin revokes it — so ANY request in the app can be the one that finds
 * out, including a background poll on a tab nobody is looking at. A `.catch` on
 * each screen is a check that has to be remembered every time a screen is added,
 * and the screen that forgets renders "auth_required" in a red box to somebody who
 * needed a login form. There is exactly one place every request passes through.
 *
 * `AuthProvider` registers `refresh` here on mount. Nothing else may.
 */
let sessionLost: (() => void) | null = null

export function setSessionLostHandler(handler: (() => void) | null): void {
  sessionLost = handler
}

/**
 * Paths where a 401 is an ANSWER and not a lost session.
 *
 *   /auth/me               401 IS the answer "nobody is signed in", and it is the
 *                          request the handler triggers. Firing here would call
 *                          `me()` from inside `me()`'s own failure, for ever.
 *   /auth/login            A wrong password is a 401. It belongs on the form the
 *                          person is typing into, not in a reset of the whole app.
 *   /auth/change-password  Same: the current-password field is checked there, and
 *                          a wrong one must not throw away the form.
 */
const SESSION_401_EXEMPT = new Set(['/auth/me', '/auth/login', '/auth/change-password'])

/**
 * Exported for `lib/auth.ts` and nothing else.
 *
 * The panel's auth routes are a different server with the same error envelope,
 * and a second copy of the body parsing below is a second copy that drifts —
 * which is exactly how the nested `{error:{code,message}}` case came to be
 * handled in one place and not the other the first time round.
 */
export async function req<T>(path: string, init: ReqInit): Promise<T> {
  const res = await fetch((init.base ?? BASE) + path, {
    method: init.method ?? 'GET',
    signal: init.signal,
    headers: init.body === undefined ? undefined : { 'content-type': 'application/json' },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  })

  if (!res.ok) {
    // The body is read as text first and only then parsed. An edge Worker that
    // threw before its handler ran returns an HTML error page with a JSON
    // content-type often enough that `res.json()` becomes the error the reader
    // sees, in place of the 500 that actually happened.
    const text = await res.text().catch(() => '')
    let message = text.slice(0, 400) || `${res.status} ${res.statusText}`
    let code: string | undefined
    try {
      // edge-api's `errorResponse` sends `{ error: { code, message } }` — NESTED.
      // Reading `parsed.error` as a string turned every error banner into
      // "[object Object]" and dropped the code, so both shapes are handled: the
      // nested envelope this server sends, and the flat one in case anything in
      // front of it (a proxy, a Worker that threw before its handler ran) sends
      // its own.
      const parsed = JSON.parse(text) as {
        error?: string | { code?: string; message?: string }
        message?: string
        code?: string
      }
      if (parsed.error !== undefined && typeof parsed.error === 'object' && parsed.error !== null) {
        message = parsed.error.message ?? message
        code = parsed.error.code
      } else {
        message = parsed.error ?? parsed.message ?? message
        code = parsed.code
      }
    } catch {
      /* not JSON; the text slice above is the best message available */
    }

    // THE SESSION HAS GONE. `auth_required` is the contract's code for a missing
    // or expired cookie; a 401 carrying NO code at all means the same thing on
    // these paths, because with the three exempt ones above set aside nothing
    // here answers 401 for any other reason — and a bare 401 is what something
    // sitting in front of the panel would send. Any other code on a 401
    // (`bad_credentials`) is about a value somebody typed and stays with the
    // screen that collected it.
    if (
      res.status === 401
      && !SESSION_401_EXEMPT.has(path)
      && (code === 'auth_required' || code === undefined)
    ) {
      sessionLost?.()
    }

    throw new ApiError(res.status, message, code, retryAfter(res))
  }

  // 204 IS A SUCCESS WITH NO BODY, and `res.json()` on an empty body throws a
  // SyntaxError — which would reach the caller as a failed request for something
  // that definitely happened. Four of the panel's auth routes answer 204.
  if (res.status === 204) return undefined as T

  return (await res.json()) as T
}

/**
 * `Retry-After` in seconds, or undefined when it is absent or unreadable.
 *
 * Both legal wire forms are read: delta-seconds, which is what the panel sends,
 * and an HTTP-date, which is what a proxy in front of it may substitute. An
 * unreadable header becomes undefined rather than 0 — "wait no time at all" is
 * the one answer that would be actively wrong, because it invites the immediate
 * retry that caused the lockout.
 */
function retryAfter(res: Response): number | undefined {
  const raw = res.headers.get('retry-after')
  if (raw === null) return undefined
  const seconds = Number(raw.trim())
  if (Number.isFinite(seconds) && seconds >= 0) return Math.round(seconds)
  const at = Date.parse(raw)
  if (Number.isNaN(at)) return undefined
  return Math.max(0, Math.round((at - Date.now()) / 1000))
}

// ============================================================================
// Sessions
// ============================================================================

export interface ListSessionsResponse {
  rows: SessionSummary[]
  total: number
  /**
   * How many rows in the WHOLE population — not this page — are in a LIVE or
   * LIVE_DEGRADED_* state.
   *
   * It exists so the client can decide whether to poll without scanning a page
   * it may not have. Deriving it from `rows` would be wrong in the one case that
   * matters: a call that starts while the reader is on page three of the history
   * would never be polled for, and the list would sit there looking finished.
   */
  live: number
}

export interface ListSessionsQuery {
  limit?: number
  offset?: number
  /** Exact `SessionState`, or the string 'live' for any LIVE* state. */
  state?: SessionState | 'live'
  agentId?: string
  /** Transport filter. The UI says web/phone; the wire says what the schema says. */
  via?: 'webrtc' | 'sip'
  /** Inclusive started_at bracket, ISO timestamps. The CLIENT converts the
      reader's local dates to instants — the server has no idea what timezone
      the person picking "22 Aug" is sitting in. */
  from?: string
  to?: string
  /** Server-side sort — it must be, the list is paginated. */
  sort?: 'started' | 'agent' | 'duration' | 'cost'
  dir?: 'asc' | 'desc'
}

/**
 * One row of `sessions`, everything the summary omits.
 *
 * Mirrors packages/db/src/schema.ts `sessions`. Token counts are the provider's
 * own accounting, summed from `response.done.usage`. Each cached figure is a
 * SUBSET of ITS OWN MODALITY's input total — `cachedAudioInputTokens` of
 * `audioInputTokens`, `cachedTextInputTokens` of `textInputTokens` — so adding a
 * cached figure to its total double-counts, and adding it to the OTHER modality's
 * total is the mistake that made this a P1: one column called
 * `cachedInputTokens` held only the audio half, the text half was thrown away
 * before it ever reached a database, and the cost shown on the session page ran
 * 21.7% high on the call it was measured against.
 */
export interface SessionDetailRow extends SessionSummary {
  model?: string | null
  tenantId: string
  toNumber: string | null
  twilioCallSid: string | null
  /** OpenAI's id for the CURRENT provider call. Nullable: unknown until the SDP
   *  exchange completes, and a reconnect mints a new one. */
  providerCallId: string | null
  /** Every provider call this logical session has had, in order. */
  providerCallIds: string[]
  stateDetail: string | null
  /** NULL until the jobs sweeper resolves the row; then 'pending' (artefacts
   *  still missing, row stays in the sweep) or 'settled' (retired). Used to be
   *  a `[sweeper:…]` text prefix ON `stateDetail`, which destroyed the reason. */
  sweepState: 'pending' | 'settled' | null
  /** When the sweeper resolved it. If nothing observed the end before then, the
   *  duration is an upper bound — the sweeper runs every five minutes. */
  sweptAt: string | null
  /** Git SHA of the build that handled the call. Without it, a deploy's
   *  WebSocket-termination burst looks like a provider incident. */
  workerVersion: string | null
  clockOffsetMs: number | null
  sidebandReattaches: number
  eventLogKey: string | null
  clientUa: string | null
  label: string | null
  audioInputTokens: number
  audioOutputTokens: number
  textInputTokens: number
  textOutputTokens: number
  /** SUBSET of `audioInputTokens`. */
  cachedAudioInputTokens: number
  /** SUBSET of `textInputTokens`. The half that was being discarded. */
  cachedTextInputTokens: number
}

/**
 * One row of `turns`. THIS IS THE "was it us or them" ANSWER, so read the
 * observer of each interval before believing it:
 *
 *   vadWaitMs      browser VAD start -> the DO's input_audio_buffer.speech_stopped
 *   admissionMs    speech_stopped -> response.created                (DO only)
 *   inferenceMs    response.created -> output_audio_buffer.started    (DO only, THE PROVIDER)
 *   networkMs      output_audio_buffer.started -> browser first playout (both ends)
 *   toolMs         function_call_arguments.done -> function_call_output (DO only)
 *
 * EVERY ONE OF THESE IS MEASURED ON OUR CLOCK. No OpenAI realtime event carries
 * a wall-clock timestamp of any kind, so there is no provider-side number to
 * reconcile against and none of these is an SLA.
 */
export interface TurnRow {
  sessionId: string
  n: number
  responseId: string | null
  userItemId: string | null
  userSpeechStoppedAt: number | null
  responseCreatedAt: number | null
  audioBufferStartedAt: number | null
  audioBufferStoppedAt: number | null
  /**
   * WAS THE PROVIDER'S OUTPUT AUDIO BUFFER ALREADY NON-EMPTY WHEN THIS TURN'S
   * `response.created` ARRIVED? Read this BEFORE `inferenceMs`, every time.
   *
   * `output_audio_buffer.started` — the far end of `inferenceMs` — is deferred
   * while the buffer drains a previous response, because responses share one
   * serialized playout queue. So on a continuation the interval is mostly the
   * previous answer's remaining playout: session 868a0c2d turn 4 recorded
   * 3,318 ms, and its `started` landed within 1 ms of the moment turn 3's audio
   * ran out. The provider's own share of that interval is unmeasurable; the five
   * clean turns of the same call median 559 ms.
   *
   * THREE STATES, ALL DISTINCT, AND NOTHING IN THIS APP MAY COLLAPSE THEM:
   *
   *   true   measured, and this turn's `inferenceMs` is NOT provider latency
   *   false  measured, and `inferenceMs` is provider latency
   *   null   NEVER OBSERVED — every turn recorded before the DO started
   *          tracking the buffer, plus any turn whose `response.created`
   *          arrived while our sideband was detached
   *
   * `null` is not `false`. Rendering it as clean would claim a measurement that
   * was never taken, on the one page whose whole argument is that it does not do
   * that.
   */
  audioBufferBusyAtCreate: boolean | null
  responseDoneAt: number | null
  clientSpeechEndAt: number | null
  clientFirstPlayoutAt: number | null
  vadWaitMs: number | null
  admissionMs: number | null
  inferenceMs: number | null
  networkMs: number | null
  toolMs: number | null
  totalToFirstAudioMs: number | null
  bargedIn: boolean
  audioOutputTokens: number | null
  truncatedAtMs: number | null
  userText: string | null
  assistantText: string | null
}

/** One row of `tool_calls`. `args` and `result` are MODEL output and TOOL output
 *  respectively; neither is evidence that anything was authorised. */
export interface ToolCallRow {
  callId: string
  sessionId: string
  turnN: number | null
  name: string
  args: unknown
  result: unknown
  ok: boolean | null
  errorMessage: string | null
  startedAt: number
  endedAt: number | null
  durationMs: number | null
  /** The response this call belonged to was cancelled mid-flight — the user
   *  barged in while the tool was running. The side effect still happened. */
  orphaned: boolean
}

export interface RecordingGap {
  fromSeq: number
  toSeq: number
  ms: number
  reason: string
}

/**
 * One row of `recordings`, keyed (sessionId, track).
 *
 * TWO INDEPENDENT ORACLES, and the reason there are two is the whole design:
 * `expectedMsFromTokens` comes off the sideband, and a sideband drop damages the
 * recording AND destroys that oracle at the same moment. `expectedMsFromRtp`
 * comes from the browser manifest's RTP anchors and is sideband-independent, so
 * something survives to measure the damage with.
 */
export interface RecordingRow {
  sessionId: string
  /**
   * Which capture this row is.
   *
   * `user`/`assistant` are the two halves of a BROWSER call, captured by the page.
   * `telephony-dual` is the carrier's file for a phone call, caller on channel 1
   * and the model on channel 2. `telephony-transfer` is a SECOND carrier file that
   * only a transferred call has: the caller and a person, recorded after the AI
   * left — a different conversation on a later clock, with no transcript, turns or
   * cost behind it. `takeover` is the same kind of second conversation for a
   * call a person took over from the panel: the caller (left) and that person
   * (right), recorded by our Durable Object as it relayed them (provenance
   * `relay`), not by a carrier.
   */
  track: 'user' | 'assistant' | 'telephony-dual' | 'telephony-transfer' | 'takeover'
  state: RecordingState
  evidence: EvidenceState
  s3Key: string | null
  bytes: number | null
  codec: string | null
  sampleRate: number | null
  expectedMsFromTokens: number | null
  expectedMsFromRtp: number | null
  actualMs: number | null
  /** actualMs / max(oracles). NULL when neither oracle was available — which is
   *  UNVERIFIED, and is NOT the same claim as a low ratio. */
  completeness: number | null
  /** ORACLE 3: non-silent ms in what we hold. NULL = not measured, NOT zero. */
  voicedMs: number | null
  /**
   * 'wire' | 'rendition' | 'carrier'. NULL on rows written before it existed.
   *
   * Read it; do not infer it. The UI used to derive the claim from the transport
   * and the track name, which is exactly how a Web Audio rendition came to be
   * described on screen as "the Opus packets OpenAI SENT".
   */
  provenance: string | null
  packetsReceived: number
  chunksReceived: number
  lastSeq: number | null
  gaps: RecordingGap[]
  manifestKey: string | null
  finalizedAt: string | null
}

/** One row of `session_events` — the SMALL queryable subset. The unsampled
 *  forensic log is one JSONL object per session in S3, at `eventLogKey`. */
export interface SessionEventRow {
  id: string
  sessionId: string
  seq: number
  atMs: number
  kind: string
  severity: string
  detail: unknown
}

export interface SessionDetail {
  session: SessionDetailRow
  turns: TurnRow[]
  toolCalls: ToolCallRow[]
  recordings: RecordingRow[]
  events: SessionEventRow[]
}

/**
 * One page of `GET /api/sessions/:id/events` — the unsampled log out of S3.
 *
 * `matchedTotal` is the size of the WHOLE object, not of this page. It is separate
 * from `returned` so a viewer cannot show a page and imply completeness; see the
 * note on `sessions.eventLog`.
 */
export interface EventLogPage {
  key: string
  rows: unknown[]
  returned: number
  /** Pass back as `after` for the next page. Null when this is the whole log. */
  nextAfter: number | null
  truncated: boolean
  /** Lines S3 held that were not parseable JSON. 1 is normal — the checkpoint
   *  boundary, where the object ends mid-line. More than 1 is a writer problem. */
  malformedLines: number
  matchedTotal: number
  filteredByKind: string | null
}

/** A short-lived signed URL for one audio track. Issued by edge-api, never by
 *  the browser — the browser has no S3 credential and no CORS grant. */
export interface AudioUrl {
  url: string
  /** ISO 8601. Displayed nowhere; used to decide whether to re-issue. */
  expiresAt: string
}

function qs(query: ListSessionsQuery): string {
  const p = new URLSearchParams()
  if (query.limit !== undefined) p.set('limit', String(query.limit))
  if (query.offset !== undefined) p.set('offset', String(query.offset))
  if (query.state !== undefined) p.set('state', query.state)
  if (query.agentId !== undefined) p.set('agentId', query.agentId)
  if (query.via !== undefined) p.set('via', query.via)
  if (query.from !== undefined) p.set('from', query.from)
  if (query.to !== undefined) p.set('to', query.to)
  if (query.sort !== undefined) p.set('sort', query.sort)
  if (query.dir !== undefined) p.set('dir', query.dir)
  const s = p.toString()
  return s ? `?${s}` : ''
}

export const sessions = {
  delegations(id: string, signal: AbortSignal) {
    return req<LiveConversation>(`/sessions/${encodeURIComponent(id)}/delegations`, { signal })
  },
  list(query: ListSessionsQuery, signal: AbortSignal) {
    return req<ListSessionsResponse>(`/sessions${qs(query)}`, { signal })
  },

  get(id: string, signal: AbortSignal) {
    return req<SessionDetail>(`/sessions/${encodeURIComponent(id)}`, { signal })
  },

  /**
   * A playback URL for one track.
   *
   * Separate from `get` on purpose. Signing three URLs costs three HMACs and a
   * short expiry, and folding them into the detail payload would mean the
   * timeline view could not be opened at all when signing failed — which is the
   * wrong dependency direction, because the latency decomposition is readable
   * with no audio at all.
   */
  audioUrl(id: string, track: string, signal: AbortSignal) {
    return req<AudioUrl>(
      `/sessions/${encodeURIComponent(id)}/audio/${encodeURIComponent(track)}`,
      { signal },
    )
  },

  /**
   * One page of the UNSAMPLED event log, read out of S3 by the Worker.
   *
   * Not part of `get`, and that is the whole point of the split: `SessionDetail.events`
   * is the small Postgres index (~18 rows a call) that the transcript needs inline for
   * its gap markers. This is the firehose — 548 events and 228 KB for one 84-second
   * call — fetched only when a human opens the panel.
   *
   * Agent config is stripped server-side. The raw object contains `session.created`
   * and `session.updated`, verbatim echoes of the effective session object, so it
   * carries the system prompt and the complete tool JSON Schemas; the endpoint is a
   * redacting proxy and deliberately NOT a presigned URL. Do not "optimise" this into
   * one.
   */
  eventLog(id: string, after: number, kind: string, signal: AbortSignal) {
    const p = new URLSearchParams()
    if (after > 0) p.set('after', String(after))
    if (kind !== '') p.set('kind', kind)
    const q = p.toString()
    return req<EventLogPage>(`/sessions/${encodeURIComponent(id)}/events${q ? `?${q}` : ''}`, {
      signal,
    })
  },

  /**
   * Mint a session. Here rather than in the call client because the response
   * shape is part of the frozen wire contract, and one typed caller is better
   * than two hand-rolled fetches that disagree about the field names.
   *
   * The body is the whole `CreateSessionRequest`, so `params` — the caller's
   * key/value facts for the agent's init webhook and HTTP tools — rides through
   * when the caller supplies it. The server bounds and never trusts it.
   */
  create(body: CreateSessionRequest, signal: AbortSignal) {
    return req<CreateSessionResponse>('/sessions', { signal, method: 'POST', body })
  },
}

// ============================================================================
// Live-call operations — the routes here that are about a call in progress
// ============================================================================
//
// Deliberately NOT methods on `sessions` above. Everything in that object reads
// the durable projection in Postgres and is consumed by the history screens;
// `endSession` and `sessionState` go through to the DO itself and are consumed
// by `lib/call`, and `monitorSession` mints the credential for the DO's monitor
// socket. Keeping them apart is not tidiness — it is a reminder that these can
// fail while every read above still works, which is the whole failure posture
// of the architecture.

/**
 * End a call ON PURPOSE. The only signal in the system that carries intent.
 *
 * Everything else that ends a call is an OBSERVATION — a socket closed, a
 * re-attach 404'd, an alarm fired — and none of those can tell a deliberate
 * hangup from a network fault, because a Realtime socket has no close frame.
 * Without this POST every ended call resolved as `ENDED_UNKNOWN` and waited on a
 * five-minute server-side sweeper to confirm what the user already knew.
 *
 * SAFE TO CALL TWICE, AND IT WILL BE. `lib/call`'s `end()` sends this AND closes
 * its sockets, on purpose. The Durable Object claims the finalisation with a
 * check-and-set, so a second arrival is a no-op that still answers 200.
 *
 * `state` is the DO's view at the moment it answered, and `null` if it answered
 * without one. Show it if useful; do not branch on it.
 */
export function endSession(id: string, reason: string, signal: AbortSignal) {
  return req<{ ok: boolean; sessionId: string; state: SessionState | null }>(
    `/sessions/${encodeURIComponent(id)}/end`,
    { signal, method: 'POST', body: { reason } },
  )
}

/**
 * The Durable Object's LIVE snapshot. A DEBUGGING SURFACE, and say so in any UI
 * that renders it.
 *
 * Every other read in this file comes from Postgres, the durable projection. This
 * one goes to the object itself and returns its working set: which sockets are up
 * this instant, how many sideband attaches have failed, how many recording bytes
 * are buffered but not yet in S3, whether the provider has ever sent an
 * `output_audio_buffer.*` event on this session. None of that exists in a table.
 *
 * THE SHAPE IS NOT A CONTRACT — hence `Record<string, unknown>`. It is whatever
 * the DO finds worth reporting this week, meant for a human to read. Render it;
 * never let a screen's correctness depend on a key being present.
 *
 * AND IT WAKES THE OBJECT: a request to a DO is inbound activity, so polling this
 * keeps an otherwise-idle session resident and billable. On demand, or a slow
 * refresh. Never a two-second timer.
 */
export function sessionState(id: string, signal: AbortSignal) {
  return req<Record<string, unknown>>(`/sessions/${encodeURIComponent(id)}/state`, { signal })
}

/** What `POST /api/sessions/:id/monitor` answers (docs/hitl.md §2). */
export interface MonitorTokenResponse {
  /** The monitor socket, `<wss origin>/ws/monitor/<id>` (relative in local development). */
  url: string
  /** Valid only at upgrade time, about two minutes; a reconnect asks again. */
  token: string
  /** Whether this token may take the call over. */
  canTakeOver: boolean
  expiresInSec: number
}

/**
 * A token to LISTEN to a live call, and perhaps take it over.
 *
 * A live-call operation like the two above, and kept here with them rather than
 * on `sessions`: it only makes sense while the call is happening (409 `not_live`
 * once it has ended) and it is consumed by the live console, not the history
 * screens. A POST, so the panel's method gate answers a viewer with 403
 * `read_only` before it reaches edge-api.
 */
export function monitorSession(id: string, signal: AbortSignal) {
  return req<MonitorTokenResponse>(`/sessions/${encodeURIComponent(id)}/monitor`, { signal, method: 'POST' })
}

// ============================================================================
// Agents and the tool registry
// ============================================================================

/**
 * What `PATCH /api/agents/:id` accepts. A subset of AgentConfig: `id`,
 * `createdAt` and `updatedAt` are the server's and are not editable, and sending
 * them would invite a handler that trusts them.
 *
 * `httpToolIds`, `transferTargetIds`, `initWebhookId` and `endWebhookId` are ID
 * REFERENCES into the panel-owned tables below (`toolDefs`, `transferTargets`,
 * `webhooks`) — the agent row never holds a URL, a header or a phone number, only
 * which rows it points at. The server validates each id on write (exists,
 * un-revoked), so a stale checkbox from a tab left open across a revoke is a
 * clean 400 rather than a silently dangling pointer.
 *
 * ⚠️ `transferTargetIds` IS ALSO A FEATURE SWITCH. An empty list is not just an
 * empty dropdown: the agent's next call carries no `transfer_to_human` tool at
 * all, so clearing it turns human handover OFF for that agent.
 *
 * `tools` is the other kind of list: built-in tool NAMES, not id references —
 * `show_info` (the screen card) is the registry's one entry today. Validated on
 * write against that registry the way the ids are validated against their
 * tables, and a feature switch in the same sense as the transfer list: a name
 * off the list means the model is never offered that tool.
 *
 * `backendModel` is GPT-Live only — the Responses model the voice model
 * delegates reasoning and tool calls to; null means the platform default
 * (gpt-5.6-terra). The wire type carries the full story; meaningless on
 * realtime-family agents and preserved rather than cleared across a family
 * switch.
 */
export type AgentPatch = Partial<
  Pick<
    AgentConfig,
    | 'name'
    | 'instructions'
    | 'voice'
    | 'model'
    | 'tools'
    | 'greeting'
    | 'greetingMode'
    | 'transcribeModel'
    | 'vadMode'
    | 'backendModel'
    | 'httpToolIds'
    | 'transferTargetIds'
    | 'initWebhookId'
    | 'endWebhookId'
    | 'transferWebhookId'
  >
>

export const agents = {
  list(signal: AbortSignal) {
    return req<{ rows: AgentConfig[] }>('/agents', { signal })
  },

  /** POST with the two required fields; the server defaults the rest (voice,
   *  model, VAD, transcription). The caller lands on the editor to fill them.
   *  `backendModel` (GPT-Live only, null = platform default) is accepted here
   *  because edge-api accepts it — the panel's create-first flow never sends
   *  it and sets it through the editor's PATCH instead. */
  create(
    body: { name: string; instructions: string; backendModel?: string | null },
    signal: AbortSignal,
  ) {
    return req<AgentConfig>('/agents', { signal, method: 'POST', body })
  },

  get(id: string, signal: AbortSignal) {
    return req<AgentConfig>(`/agents/${encodeURIComponent(id)}`, { signal })
  },

  /** PATCH, not PUT: the form edits eight fields of a row with more than eight
   *  columns, and a PUT would have to invent values for the rest. */
  update(id: string, patch: AgentPatch, signal: AbortSignal) {
    return req<AgentConfig>(`/agents/${encodeURIComponent(id)}`, {
      signal,
      method: 'PATCH',
      body: patch,
    })
  },

  /** Remove the agent's greeting clip. Clips are made by a greeting-render
   *  call (GreetingGenerator), never uploaded. */
  deleteGreetingAudio(id: string, signal: AbortSignal) {
    return req<void>(`/agents/${encodeURIComponent(id)}/greeting-audio`, { signal, method: 'DELETE' })
  },
}

/** Where the panel fetches a greeting clip by its content hash. */
export function greetingAudioPath(sha256: string): string {
  return `/api/greeting-audio/${encodeURIComponent(sha256)}`
}

// ============================================================================
// HTTP tool definitions and webhook endpoints — panel-owned, under /api anyway
// ============================================================================
//
// WHO ANSWERS THESE: the panel worker itself, exactly like `/api/users` — it
// matches `/api/tool-defs` and `/api/webhooks` BEFORE the forward, so they never
// reach edge-api. They still use this file's default `/api` base because the
// contract puts them under that prefix; `base: ''` is reserved for the `/auth/*`
// routes in lib/auth.ts, where the prefix says a cookie is being set or cleared.
// They live in THIS file rather than lib/auth.ts because that file is the HUMAN
// half of the panel — who may sign in — and these are configuration resources
// like agents: things a call is built from, not people.
//
// THE TYPES ARE DECLARED HERE AND MIRROR THE PANEL WORKER — `ToolDefView` and
// the create/patch bodies restate workers/panel/src/tools.ts, `WebhookView`
// restates workers/panel/src/webhooks.ts. Restated deliberately: the boundary
// rule (scripts/check-boundaries.mjs) exists precisely so the browser bundle
// imports nothing that lives beside a database schema, and worker source sits
// beside @voiceai/db. If the two ever disagree, the worker wins and this file
// is wrong.
//
// HEADERS ARE WRITE-ONLY, and every client below inherits that: creates and
// patches SEND `headers: Record<string,string>` (the values are credentials for
// the customer's endpoint), reads return `headerNames: string[]` and never a
// value. On PATCH, omitting `headers` preserves what is stored and sending `{}`
// clears it — which is what lets a form PATCH back a row it read, because a
// read never contains values to lose.

/** One row of `tool_definitions`, as the panel serves it. Mirrors
 *  `ToolDefView` in workers/panel/src/tools.ts — see the section header. */
export interface ToolDefView {
  id: string
  name: string
  description: string
  /** Human-facing label for widgets and lists; null when unset. Informational
   *  only — the model keeps reading `name` and `description`. */
  displayName: string | null
  /** Human-facing short blurb, same audience as `displayName`. */
  displayDescription: string | null
  url: string
  /** Names only, sorted. The values never leave the server. */
  headerNames: string[]
  /** Whether the executor POSTs the transcript-so-far with each call. */
  includeTranscript: boolean
  /** Whether each call's POST body carries the session's client data params. */
  sendClientPayload: boolean
  /** The reversible off-switch: false = attached agents keep the reference but
   *  new sessions skip the tool. Distinct from revocation, which is forever. */
  enabled: boolean
  /** The tool's JSON Schema. Must be an object-type schema, server-checked. */
  parameters: Record<string, unknown>
  createdAt: string
  revokedAt: string | null
}

/** One row of `webhook_endpoints`. Mirrors `WebhookView` in
 *  workers/panel/src/webhooks.ts — see the section header. */
export interface WebhookView {
  id: string
  name: string
  url: string
  /** Names only, sorted. The values never leave the server. */
  headerNames: string[]
  /**
   * OUR secret, minted at create time and shown so somebody can paste it into
   * the receiving service — the receiver uses it to verify our signature on
   * each delivery. Shown in full on reads deliberately; it is shared WITH the
   * customer, unlike header values, which are the customer's own credentials
   * and are write-only.
   */
  signingSecret: string
  /** The reversible off-switch: an inactive endpoint keeps its agent references
   *  but neither the init nor the end-of-session webhook fires it. */
  enabled: boolean
  createdAt: string
  revokedAt: string | null
}

export interface CreateToolDefBody {
  /** `^[a-z0-9][a-z0-9_-]{0,63}$`, unique across the table — a revoked tool's
   *  name is never reissued. */
  name: string
  description: string
  /** Optional display label/blurb for widgets. Informational only. */
  displayName?: string
  displayDescription?: string
  url: string
  /** Full values. Omitted means none. */
  headers?: Record<string, string>
  parameters: Record<string, unknown>
  includeTranscript?: boolean
  /** Defaults to true server-side — that was the pre-flag behaviour. */
  sendClientPayload?: boolean
}

export type PatchToolDefBody = Partial<{
  /** Renaming is allowed: past calls stay recorded under the name that was
   *  current at call time, and the old name becomes free. The new name is
   *  validated and uniqueness-checked like a created one. */
  name: string
  description: string
  /** Omitted = keep, null = clear. The server also treats '' as clear. */
  displayName: string | null
  displayDescription: string | null
  url: string
  /** Omitted = preserve stored values, `{}` = clear. See the section header. */
  headers: Record<string, string>
  parameters: Record<string, unknown>
  includeTranscript: boolean
  sendClientPayload: boolean
  enabled: boolean
}>

export interface CreateWebhookBody {
  name: string
  url: string
  headers?: Record<string, string>
}

/** `signingSecret` is deliberately absent: the server refuses to change it,
 *  because the receiver holds a pasted copy that would silently stop
 *  verifying. Rotation means revoke-and-create. */
export type PatchWebhookBody = Partial<{
  name: string
  url: string
  /** Omitted = preserve stored values, `{}` = clear. See the section header. */
  headers: Record<string, string>
  enabled: boolean
}>

export const toolDefs = {
  /** Live definitions only. Revoked rows keep their burned names server-side
   *  but are nobody's business on a list of what can be attached to an agent. */
  list(signal: AbortSignal) {
    return req<{ rows: ToolDefView[] }>('/tool-defs', { signal })
  },
  /** One definition, revoked rows included — the detail page's read. */
  get(id: string, signal: AbortSignal) {
    return req<ToolDefView>(`/tool-defs/${encodeURIComponent(id)}`, { signal })
  },
  create(body: CreateToolDefBody, signal: AbortSignal) {
    return req<ToolDefView>('/tool-defs', { signal, method: 'POST', body })
  },
  update(id: string, body: PatchToolDefBody, signal: AbortSignal) {
    return req<ToolDefView>(`/tool-defs/${encodeURIComponent(id)}`, {
      signal,
      method: 'PATCH',
      body,
    })
  },
  /** DELETE is a REVOKE: the row survives with `revokedAt` set, its name stays
   *  burned, and sessions already running keep their snapshot of it. */
  revoke(id: string, signal: AbortSignal) {
    return req<unknown>(`/tool-defs/${encodeURIComponent(id)}`, { signal, method: 'DELETE' })
  },
  /** One rehearsal POST to the tool's endpoint, fired by the panel with the
   *  stored headers (which never reach this browser). The upstream's status is
   *  DATA in the result, not this request's status. */
  test(id: string, args: Record<string, unknown>, signal: AbortSignal) {
    return req<ToolTestResult>(`/tool-defs/${encodeURIComponent(id)}/test`, {
      signal,
      method: 'POST',
      body: { args },
    })
  },
}

// ============================================================================
// Transfer targets — panel-owned, same shape as the two above
// ============================================================================
//
// ONE DIFFERENCE FROM TOOLS AND WEBHOOKS, and it goes the other way: nothing here
// is write-only. `destination` is returned in full on every read and shown in the
// UI, because an operator cannot check a desk number they are not allowed to see.
// The number is kept away from the MODEL — the transfer tool offers it an enum of
// target NAMES and nothing else — which is a different boundary from the one
// `headers` sits behind, and it is enforced server-side where the session config
// is built, not here.

/** One row of `transfer_targets`. Mirrors `TransferTargetView` in
 *  workers/panel/src/transfers.ts — see the section header above `ToolDefView`
 *  for why the type is restated rather than imported. */
export interface TransferTargetView {
  id: string
  /** What the MODEL names. Spaces and capitals allowed; it is an enum value, not
   *  an identifier. Not renameable once created — past transfers are recorded
   *  under it. */
  name: string
  /** Prose telling the model when to pick this desk over the others. Required:
   *  without it a multi-target enum is a coin toss. */
  description: string
  /** E.164, in full. See the section header on why this is not write-only. */
  destination: string
  /** A fixed line read to the answering human, or null to use the sentence the
   *  model wrote about this particular caller. */
  whisper: string | null
  /** How long the desk's phone rings before the caller is put back to the AI. */
  ringSeconds: number
  /** The reversible off-switch. ⚠️ Disabling the LAST enabled target of an agent
   *  removes the transfer tool from that agent's next call entirely. */
  enabled: boolean
  createdAt: string
  revokedAt: string | null
}

export interface CreateTransferTargetBody {
  name: string
  description: string
  destination: string
  whisper?: string | null
  ringSeconds?: number
}

/** `name` is absent on purpose — the server refuses a rename. `whisper: null`
 *  clears an operator script and returns the desk to the model's own sentence. */
export type PatchTransferTargetBody = Partial<{
  description: string
  destination: string
  whisper: string | null
  ringSeconds: number
  enabled: boolean
}>

export const transferTargets = {
  /** Live targets only, for the agent picker and the list page. */
  list(signal: AbortSignal) {
    return req<{ rows: TransferTargetView[] }>('/transfer-targets', { signal })
  },
  /** One target, revoked rows included — what a transferred session links to. */
  get(id: string, signal: AbortSignal) {
    return req<TransferTargetView>(`/transfer-targets/${encodeURIComponent(id)}`, { signal })
  },
  create(body: CreateTransferTargetBody, signal: AbortSignal) {
    return req<TransferTargetView>('/transfer-targets', { signal, method: 'POST', body })
  },
  update(id: string, body: PatchTransferTargetBody, signal: AbortSignal) {
    return req<TransferTargetView>(`/transfer-targets/${encodeURIComponent(id)}`, {
      signal,
      method: 'PATCH',
      body,
    })
  },
  /** DELETE is a REVOKE: the row survives, its name stays burned, and a transfer
   *  already armed on a live call completes to the number it captured. */
  revoke(id: string, signal: AbortSignal) {
    return req<unknown>(`/transfer-targets/${encodeURIComponent(id)}`, { signal, method: 'DELETE' })
  },
}

/** Mirrors testToolDef in workers/panel/src/tools.ts. */
export interface ToolTestResult {
  /** 2xx upstream AND parseable JSON — what a live tool call needs to succeed. */
  ok: boolean
  /** Upstream HTTP status, or null when nothing answered within the timeout. */
  status: number | null
  durationMs: number
  /** The parsed JSON the model would be handed, or null when unparseable. */
  body: unknown
  error: string | null
}

export const webhooks = {
  list(signal: AbortSignal) {
    return req<{ rows: WebhookView[] }>('/webhooks', { signal })
  },
  /** One row, revoked included — the detail page stays readable after revoke. */
  get(id: string, signal: AbortSignal) {
    return req<WebhookView>(`/webhooks/${encodeURIComponent(id)}`, { signal })
  },
  create(body: CreateWebhookBody, signal: AbortSignal) {
    return req<WebhookView>('/webhooks', { signal, method: 'POST', body })
  },
  /** One signed rehearsal delivery. Same result envelope as toolDefs.test. */
  test(id: string, params: Record<string, string>, signal: AbortSignal) {
    return req<ToolTestResult>(`/webhooks/${encodeURIComponent(id)}/test`, {
      signal,
      method: 'POST',
      body: { params },
    })
  },
  update(id: string, body: PatchWebhookBody, signal: AbortSignal) {
    return req<WebhookView>(`/webhooks/${encodeURIComponent(id)}`, {
      signal,
      method: 'PATCH',
      body,
    })
  },
  /** Revoke, same contract as toolDefs.revoke. */
  revoke(id: string, signal: AbortSignal) {
    return req<unknown>(`/webhooks/${encodeURIComponent(id)}`, { signal, method: 'DELETE' })
  },
}

// ============================================================================
// Telephony — Twilio credentials and the phone-number inventory, panel-owned
// ============================================================================
//
// Same arrangement as the tool and webhook registries above: the panel worker
// answers these under /api itself, and the types below are RESTATED from the
// worker's telephony module, not imported; if the two ever disagree the worker
// wins and this file is wrong.

/** The stored Twilio credential pair, as much of it as a browser may see.
 *  Restated from the panel worker — see the section header. */
export interface CredentialsView {
  /** The Account SID, or null when nothing is configured yet. A PUBLIC
   *  identifier — it appears in every Twilio console URL — so it is shown. */
  accountSid: string | null
  /** Whether an auth token is stored. The token itself NEVER leaves the
   *  server; this boolean is the whole read surface. */
  authTokenSet: boolean
  updatedAt: string | null
  updatedBy: string | null
}

/** One phone number the account holds (or held — released rows survive).
 *  Restated from the panel worker — see the section header. */
export interface PhoneNumberView {
  /** Twilio's id for the number. The key everywhere; the number itself can
   *  in principle be re-bought and appear twice. */
  sid: string
  /** E.164, e.g. +15005550006. */
  number: string
  friendlyName: string | null
  /** Which agent answers calls to it, or null for unassigned. */
  agentId: string | null
  agentName: string | null
  /** The Voice webhook URL Twilio currently holds for the number. */
  voiceUrl: string | null
  /** Whether that URL points at us: true = ours, false = points elsewhere,
   *  null = not checked. Three states — null is NOT false. */
  webhookOk: boolean | null
  status: 'active' | 'released'
  createdAt: string
  updatedAt: string
  releasedAt: string | null
}

/** One number Twilio offers for purchase.
 *  Restated from the panel worker — see the section header. */
export interface AvailableNumberView {
  number: string
  friendlyName: string | null
  locality: string | null
  region: string | null
  isoCountry: string
}

export const telephony = {
  credentials(signal: AbortSignal) {
    return req<CredentialsView>('/telephony/credentials', { signal })
  },
  /** PUT the pair. `authToken` omitted or empty keeps the stored token, so a
   *  form can re-save the SID it read without ever holding the secret.
   *  Admin-only server-side (403 `admin_only`). */
  saveCredentials(body: { accountSid: string; authToken?: string }, signal: AbortSignal) {
    return req<CredentialsView>('/telephony/credentials', { signal, method: 'PUT', body })
  },
  /** The stored inventory — what the panel already knows, no Twilio call. */
  numbers(signal: AbortSignal) {
    return req<{ rows: PhoneNumberView[] }>('/telephony/numbers', { signal })
  },
  /** Re-pull the inventory from Twilio and return the refreshed rows. */
  sync(signal: AbortSignal) {
    return req<{ rows: PhoneNumberView[] }>('/telephony/numbers/sync', {
      signal,
      method: 'POST',
    })
  },
  /** Numbers Twilio offers for sale. Reads Twilio live; costs nothing. */
  available(
    q: { country: string; contains?: string; type: 'local' | 'tollfree' },
    signal: AbortSignal,
  ) {
    const p = new URLSearchParams()
    p.set('country', q.country)
    if (q.contains !== undefined && q.contains !== '') p.set('contains', q.contains)
    p.set('type', q.type)
    return req<{ rows: AvailableNumberView[] }>(`/telephony/numbers/available?${p.toString()}`, {
      signal,
    })
  },
  /** BUYS the number. Real money on the Twilio account, every call. */
  purchase(number: string, signal: AbortSignal) {
    return req<PhoneNumberView>('/telephony/numbers', {
      signal,
      method: 'POST',
      body: { number },
    })
  },
  /** Point the number at an agent, or at null to unassign. Takes effect on
   *  the next inbound call — no Save step anywhere. */
  assign(sid: string, agentId: string | null, signal: AbortSignal) {
    return req<PhoneNumberView>(`/telephony/numbers/${encodeURIComponent(sid)}`, {
      signal,
      method: 'PATCH',
      body: { agentId },
    })
  },
  /** Rewrite the number's Voice webhook URL on Twilio to point back at us. */
  repairWebhook(sid: string, signal: AbortSignal) {
    return req<PhoneNumberView>(`/telephony/numbers/${encodeURIComponent(sid)}/webhook`, {
      signal,
      method: 'POST',
    })
  },
  /** RELEASES the number at Twilio — irreversible; the number goes back to
   *  the carrier pool. Answers 204; the row survives as status 'released'. */
  release(sid: string, signal: AbortSignal) {
    return req<void>(`/telephony/numbers/${encodeURIComponent(sid)}`, {
      signal,
      method: 'DELETE',
    })
  },
}
